import type { ActiveCall, Channel } from '@nexo/types';
import { AvatarStack, IconButton, cn, useToast } from '@nexo/ui';
import { Phone, Video } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { channelLabel } from '../channels/ChannelSidebar';
import { useActiveCalls, useCallsConfig } from '../../lib/queries';
import { callStore, useCall } from './callStore';
import { canIn } from '../../lib/permissions';

const CallRoom = lazy(() => import('./CallRoom'));

/** The ongoing call of a conversation, if anyone is in it. */
export function useChannelCall(channelId: string): ActiveCall | undefined {
  const { team } = useTeam();
  const calls = useActiveCalls(team.id);
  return calls.data?.find((c) => c.channelId === channelId && c.participants.length > 0);
}

/** Starts or joins a call, reporting errors and the browser hand-off with toasts. */
export function useStartCall() {
  const toast = useToast();
  const { channels } = useTeam();
  const { t } = useTranslation();
  return async (channelId: string, video: boolean) => {
    const channel = channels.find((c) => c.id === channelId);
    const result = await callStore.start(
      channelId,
      video,
      channel ? channelLabel(channel) : undefined,
    );
    if (!result.ok) {
      toast({
        title: t('No se pudo entrar en la llamada'),
        description: result.error,
        tone: 'error',
      });
    } else if (result.external) {
      toast({
        title: t('Llamada abierta en tu navegador'),
        description: t(
          'La app de escritorio en Linux no admite videollamadas; continúa en el navegador.',
        ),
      });
    }
  };
}

/** Voice / video call buttons for a conversation header. */
export function CallControls({ channel }: { channel: Channel }) {
  const config = useCallsConfig();
  const current = useCall();
  const live = useChannelCall(channel.id);
  const startCall = useStartCall();
  const { t } = useTranslation();

  if (!config.data?.enabled || !canIn(channel, 'connect')) return null;

  const start = (video: boolean) => startCall(channel.id, video);

  if (current?.join.channelId === channel.id) {
    return (
      <button
        type="button"
        onClick={() => callStore.setExpanded(true)}
        className="mr-1 inline-flex h-8 items-center gap-2 rounded-full bg-green-600 px-3 text-[13px] font-medium text-white hover:bg-green-700"
      >
        <span className="size-1.5 animate-pulse rounded-full bg-white" /> {t('En la llamada')}
      </button>
    );
  }

  if (live) {
    return (
      <button
        type="button"
        onClick={() => void start(false)}
        title={t('Unirse a la llamada')}
        className="mr-1 inline-flex h-8 items-center gap-2 rounded-full bg-green-600 py-0 pr-3 pl-1 text-[13px] font-medium text-white hover:bg-green-700"
      >
        <AvatarStack people={live.participants} max={3} size="xs" />
        <Phone className="size-3.5" /> {t('Unirse')}
      </button>
    );
  }

  return (
    <>
      <IconButton label={t('Llamada de voz')} onClick={() => void start(false)}>
        <Phone />
      </IconButton>
      <IconButton label={t('Videollamada')} onClick={() => void start(true)}>
        <Video />
      </IconButton>
    </>
  );
}

/** Small "call in progress" marker for the sidebar. */
export function CallIndicator({ channelId, className }: { channelId: string; className?: string }) {
  const live = useChannelCall(channelId);
  const { t } = useTranslation();
  if (!live) return null;
  return (
    <span
      title={t('En llamada: {{names}}', {
        names: live.participants.map((p) => p.displayName).join(', '),
      })}
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-full bg-green-600/15 px-1.5 text-[11px] font-medium text-green-700 dark:text-green-400',
        className,
      )}
    >
      <Phone className="size-3" />
      {live.participants.length}
    </span>
  );
}

/** Mounts the live call (lazy: LiveKit is only downloaded when someone joins a call). */
export function CallDock() {
  const call = useCall();
  if (!call) return null;
  return (
    <Suspense fallback={null}>
      <CallRoom key={call.join.channelId} call={call} />
    </Suspense>
  );
}
